import React, { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import api from '../services/api';

const ContractorCommissionModal = ({ isOpen, onClose, contractor, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [constructionTypes, setConstructionTypes] = useState([]);
  const [commissionRates, setCommissionRates] = useState({});
  const [hasCustomRates, setHasCustomRates] = useState(false);
  const [feeType, setFeeType] = useState('commission');
  const [monthlyAmount, setMonthlyAmount] = useState('');
  const [paymentDay, setPaymentDay] = useState(5);
  const [accountInfo, setAccountInfo] = useState({
    bank: '',
    account: '',
    depositor: ''
  });

  // 설정에서 공사 유형 목록 가져오기
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await api.get('/admin/settings');
        console.log('Settings response:', response.data);
        const types = response.data.construction_types?.split(',').map(type => type.trim()) || [];
        console.log('Construction types:', types);
        setConstructionTypes(types);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // 소장별 수수료 설정 가져오기
  useEffect(() => {
    if (contractor && isOpen) {
      fetchContractorCommissionSettings();
      fetchContractorCommissionRates();
    }
  }, [contractor, isOpen]);

  const fetchContractorCommissionSettings = async () => {
    try {
      const response = await api.get(`/admin/contractors/${contractor.id}/commission-settings`);
      const settings = response.data;
      setFeeType(settings.fee_type || 'commission');
      setMonthlyAmount(settings.monthly_amount || '');
      setPaymentDay(settings.payment_day || 5);
      setAccountInfo({
        bank: settings.bank || '',
        account: settings.account || '',
        depositor: settings.depositor || ''
      });
    } catch (error) {
      console.error('Error fetching contractor commission settings:', error);
    }
  };

  const fetchContractorCommissionRates = async () => {
    try {
      const response = await api.get(`/admin/contractors/${contractor.id}/commission-rates`);
      const rates = response.data.rates || {};
      setCommissionRates(rates);
      setHasCustomRates(response.data.hasCustomRates || false);
    } catch (error) {
      console.error('Error fetching contractor commission rates:', error);
      setCommissionRates({});
    }
  };

  const handleAddRate = (type) => {
    const newRates = { ...commissionRates };
    if (!newRates[type]) {
      newRates[type] = [];
    }
    newRates[type].push({
      min_amount: 0,
      rate_percentage: 0
    });
    setCommissionRates(newRates);
  };

  const handleRemoveRate = (type, index) => {
    const newRates = { ...commissionRates };
    newRates[type].splice(index, 1);
    if (newRates[type].length === 0) {
      delete newRates[type];
    }
    setCommissionRates(newRates);
  };

  const handleRateChange = (type, index, field, value) => {
    const newRates = { ...commissionRates };
    if (!newRates[type]) newRates[type] = [];
    if (!newRates[type][index]) newRates[type][index] = {};
    
    newRates[type][index][field] = field === 'min_amount' ? 
      parseInt(value.replace(/,/g, '') || '0') : 
      parseFloat(value || '0');
    
    setCommissionRates(newRates);
  };

  const handleClearAll = () => {
    if (window.confirm('모든 개별 수수료 설정을 삭제하고 기본 설정을 사용하시겠습니까?')) {
      setCommissionRates({});
      setHasCustomRates(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // 수수료 타입별 저장
      await api.put(`/admin/contractors/${contractor.id}/commission-settings`, {
        fee_type: feeType,
        monthly_amount: feeType === 'monthly' ? parseFloat(monthlyAmount) : null,
        payment_day: feeType === 'monthly' ? parseInt(paymentDay) : null,
        bank: accountInfo.bank || null,
        account: accountInfo.account || null,
        depositor: accountInfo.depositor || null
      });

      // 프로젝트별 수수료인 경우만 수수료율 저장
      if (feeType === 'commission') {
        // 빈 값 제거 및 정렬
        const cleanedRates = {};
        Object.keys(commissionRates).forEach(type => {
          const validRates = commissionRates[type]
            .filter(rate => rate.min_amount >= 0 && rate.rate_percentage > 0)
            .sort((a, b) => a.min_amount - b.min_amount);
          
          if (validRates.length > 0) {
            cleanedRates[type] = validRates;
          }
        });

        await api.put(`/admin/contractors/${contractor.id}/commission-rates`, {
          rates: cleanedRates
        });
      }

      toast.success('수수료 설정이 저장되었습니다.');
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving commission settings:', error);
      toast.error('수수료 설정 저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ko-KR').format(num);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="relative z-50"
    >
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <Dialog.Panel className="mx-auto max-w-4xl w-full max-h-[90vh] overflow-y-auto bg-white rounded-lg shadow-xl">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold">
              {contractor?.name} 소장 수수료 설정
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6">
            {/* 수수료 방식 선택 */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">수수료 방식</h3>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="commission"
                    checked={feeType === 'commission'}
                    onChange={(e) => setFeeType(e.target.value)}
                    className="mr-2"
                  />
                  <span>프로젝트별 수수료</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="monthly"
                    checked={feeType === 'monthly'}
                    onChange={(e) => setFeeType(e.target.value)}
                    className="mr-2"
                  />
                  <span>월 정액</span>
                </label>
              </div>
            </div>

            {/* 월정액 설정 */}
            {feeType === 'monthly' && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      월 납부액
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={formatNumber(monthlyAmount || 0)}
                        onChange={(e) => setMonthlyAmount(e.target.value.replace(/,/g, ''))}
                        className="flex-1 px-3 py-2 border rounded"
                        placeholder="0"
                      />
                      <span>원</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      납부일
                    </label>
                    <div className="flex items-center gap-2">
                      <span>매월</span>
                      <select
                        value={paymentDay}
                        onChange={(e) => setPaymentDay(e.target.value)}
                        className="px-3 py-2 border rounded"
                      >
                        {[...Array(31)].map((_, i) => (
                          <option key={i + 1} value={i + 1}>{i + 1}</option>
                        ))}
                      </select>
                      <span>일</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 프로젝트별 수수료 설정 */}
            {feeType === 'commission' && (
              <>
                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    {hasCustomRates ? 
                      '개별 수수료율이 설정되어 있습니다. 설정된 공사 유형은 개별 요율이 적용되며, 나머지는 기본 수수료율이 적용됩니다.' :
                      '개별 수수료율이 설정되지 않았습니다. 모든 공사에 기본 수수료율이 적용됩니다.'
                    }
                  </p>
                  {hasCustomRates && (
                    <button
                      onClick={handleClearAll}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                    >
                      모든 개별 설정 삭제
                    </button>
                  )}
                </div>

                <div className="space-y-6">
                  {constructionTypes.map((type) => (
                <div key={type} className="border rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-medium">{type}</h3>
                    <button
                      onClick={() => handleAddRate(type)}
                      className="text-sm bg-indigo-600 text-white px-3 py-1 rounded hover:bg-indigo-700"
                    >
                      구간 추가
                    </button>
                  </div>

                  {commissionRates[type] && commissionRates[type].length > 0 ? (
                    <div className="space-y-2">
                      {commissionRates[type].map((rate, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="최소 금액"
                            value={formatNumber(rate.min_amount || 0)}
                            onChange={(e) => handleRateChange(type, index, 'min_amount', e.target.value)}
                            className="flex-1 px-3 py-2 border rounded"
                          />
                          <span>원 이상</span>
                          <input
                            type="number"
                            placeholder="수수료율"
                            value={rate.rate_percentage || ''}
                            onChange={(e) => handleRateChange(type, index, 'rate_percentage', e.target.value)}
                            className="w-20 px-3 py-2 border rounded"
                            step="0.1"
                            min="0"
                            max="100"
                          />
                          <span>%</span>
                          <button
                            onClick={() => handleRemoveRate(type, index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            삭제
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">기본 수수료율 적용</p>
                  )}
                </div>
                  ))}
                </div>
              </>
            )}

            {/* 계좌 정보 설정 */}
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-lg font-medium mb-4">납부 계좌 정보</h3>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-600">
                  이 소장의 전용 계좌 정보를 설정할 수 있습니다. 
                  비워두면 기본 계좌 정보가 사용됩니다.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    은행명
                  </label>
                  <input
                    type="text"
                    value={accountInfo.bank}
                    onChange={(e) => setAccountInfo({ ...accountInfo, bank: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="예: 국민은행"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    계좌번호
                  </label>
                  <input
                    type="text"
                    value={accountInfo.account}
                    onChange={(e) => setAccountInfo({ ...accountInfo, account: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="예: 123-456-789012"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    예금주
                  </label>
                  <input
                    type="text"
                    value={accountInfo.depositor}
                    onChange={(e) => setAccountInfo({ ...accountInfo, depositor: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    placeholder="예: (주)건기"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:text-gray-900"
            >
              취소
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? '저장 중...' : '저장'}
            </button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default ContractorCommissionModal;